<template>
    <div class="notification-main">
        <NotificationFilters
            :unread-only="filters.unreadOnly"
            :total-count="pagination.totalCount"
            :unread-count="unreadCount"
            @apply="onFilterApply"
        />
        <NotificationTable :notifications="notifications" :loading="loading" @row-click="openDetail" />

        <!-- 페이징 -->
        <TablePagination
            v-if="pagination.totalCount > 0"
            :total-items="pagination.totalCount"
            :current-page="pagination.page"
            :page-size="pageSize"
            :page-size-options="[10, 20, 50, 100]"
            @update:currentPage="goPage($event)"
            @update:pageSize="onPageSizeChangeFromChild($event)"
        />

        <!-- 상세 모달 -->
        <NotificationDetailModal
            :visible="detailVisible"
            :detail="detailData"
            :loading="detailLoading"
            :error="detailError"
            @close="detailVisible = false"
            @navigate-workflow="handleNavigateWorkflow"
        />
    </div>
</template>

<script>
import carbonApi from "../../../api";
import NotificationFilters from "../views/NotificationFilters.vue";
import NotificationTable from "../views/NotificationTable.vue";
import NotificationDetailModal from "../modals/NotificationDetailModal.vue";
import TablePagination from "../../common/TablePagination.vue";

export default {
    name: "NotificationMain",
    components: {
        NotificationFilters,
        NotificationTable,
        NotificationDetailModal,
        TablePagination,
    },
    props: {
        loginUser: { type: Object, default: null },
    },
    data() {
        return {
            filters: { unreadOnly: false },
            pageSize: 10,
            notifications: [],
            pagination: { page: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
            unreadCount: 0,
            loading: false,
            detailVisible: false,
            detailData: null,
            detailLoading: false,
            detailError: "",
        };
    },
    mounted() {
        this.fetchNotifications();
        this.fetchUnreadCount();
    },
    methods: {
        onFilterApply(payload) {
            this.filters.unreadOnly = payload.unreadOnly ?? this.filters.unreadOnly;
            this.pagination.page = 1;
            this.fetchNotifications();
        },
        async fetchNotifications() {
            if (!this.loginUser?.userIdx) return;
            this.loading = true;
            try {
                const params = {
                    page: this.pagination.page,
                    pageSize: this.pageSize,
                    unreadOnly: this.filters.unreadOnly ? true : undefined,
                };
                const res = await carbonApi.notification.getNotifications(params);
                const data = res?.data?.data ?? res?.data ?? res;
                const items = data?.items ?? data?.rows ?? [];
                const pag = data?.pagination ?? {};
                this.notifications = items;
                this.pagination = {
                    page: pag.page ?? this.pagination.page,
                    pageSize: pag.limit ?? pag.pageSize ?? this.pageSize,
                    totalCount: pag.total ?? 0,
                    totalPages: pag.totalPages ?? 1,
                };
            } catch (err) {
                console.error("알림 목록 조회 실패:", err);
                this.notifications = [];
                const msg = err?.response?.data?.message || err?.message || "목록 조회에 실패했습니다.";
                if (this.$bvToast?.toast) {
                    this.$bvToast.toast(msg, { variant: "danger", title: "오류" });
                } else {
                    alert(msg);
                }
            } finally {
                this.loading = false;
            }
        },
        async fetchUnreadCount() {
            if (!this.loginUser?.userIdx) return;
            try {
                const res = await carbonApi.notification.getUnreadCount();
                const data = res?.data?.data ?? res?.data ?? res;
                this.unreadCount = data?.unreadCount ?? 0;
            } catch {
                this.unreadCount = 0;
            }
        },
        openDetail(row) {
            this.detailVisible = true;
            this.detailData = row;
            this.detailLoading = false;
            this.detailError = "";
            if (!row.isRead) {
                carbonApi.notification
                    .markAsRead(row.notificationIdx)
                    .then(() => {
                        row.isRead = 1;
                        this.unreadCount = Math.max(0, this.unreadCount - 1);
                    })
                    .catch(() => {});
            }
        },
        handleNavigateWorkflow(workflowIdx) {
            this.detailVisible = false;
            this.$emit("navigate-workflow", workflowIdx);
        },
        goPage(page) {
            this.pagination.page = page;
            this.fetchNotifications();
        },
        onPageSizeChangeFromChild(newSize) {
            this.pageSize = newSize;
            this.pagination.page = 1;
            this.pagination.pageSize = newSize;
            this.fetchNotifications();
        },
        refresh() {
            this.fetchNotifications();
            this.fetchUnreadCount();
        },
    },
};
</script>

<style scoped>
.notification-main {
    padding: 10px 16px;
    min-height: 200px;
}
</style>
